/**
 * Lightweight analytics dispatcher.
 * Pushes events to GA4 / GTM dataLayer when available, plus a CustomEvent
 * (window 'analytics:event') so any other tracker can subscribe. Falls back
 * to console.log in dev so events are visible during development.
 */
import { logger } from "@/lib/logger";

type EventParams = Record<string, string | number | boolean | undefined | null>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown> | unknown[]>;
    gtag?: (...args: unknown[]) => void;
  }
}

export function trackEvent(eventName: string, params: EventParams = {}): void {
  const payload = { event: eventName, ...params, timestamp: Date.now() };

  try {
    if (typeof window === "undefined") return;

    // GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    // GA4 gtag
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }

    // Generic listener hook
    window.dispatchEvent(new CustomEvent("analytics:event", { detail: payload }));

    logger.info(`[analytics] ${eventName}`, params);
  } catch (err) {
    logger.error("Failed to dispatch analytics event", err);
  }
}

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  currency?: string;
}

/** GA4-style purchase intent event. */
export function trackBeginCheckout(
  items: EcommerceItem[],
  context: EventParams = {},
): void {
  const value = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const currency = items[0]?.currency || "USD";
  trackEvent("begin_checkout", {
    currency,
    value: Number(value.toFixed(2)),
    items_count: items.length,
    ...context,
  });
}

/** Generic CTA click. */
export function trackCtaClick(
  ctaId: string,
  context: EventParams = {},
): void {
  trackEvent("cta_click", { cta_id: ctaId, ...context });
}
